import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError } from '@/api/client';
import { employeesApi, organizationApi } from '@/api/endpoints';
import { queryKeys } from '@/api/queryKeys';
import type { Employee } from '@/api/types';
import { Plus } from 'lucide-react';
import { usePermission } from '@/auth/AuthProvider';
import { PERMISSIONS } from '@/auth/permissions';
import { Button } from '@/design-system/Button';
import { InlineError } from '@/design-system/Feedback';
import { Select, TextInput } from '@/design-system/Inputs';
import { Dialog } from '@/design-system/Overlay';
import { PhotoPicker } from './PhotoPicker';
import { QuickLocationDialog } from '../assets/QuickCatalogDialogs';

interface FormValues {
  employeeNumber: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  jobTitle: string;
  departmentId: string;
  locationId: string;
  joinedOn: string;
}

export function CreateEmployeeDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (employee: Employee) => void;
}) {
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [photo, setPhoto] = useState<File | null>(null);

  const canEditOrg = usePermission(PERMISSIONS.ORG_WRITE);
  const [addingLocation, setAddingLocation] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      employeeNumber: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      jobTitle: '',
      departmentId: '',
      locationId: '',
      joinedOn: '',
    },
  });

  const departments = useQuery({
    queryKey: queryKeys.organization.departments(),
    queryFn: organizationApi.departments,
    enabled: open,
    staleTime: 10 * 60_000,
  });

  const locations = useQuery({
    queryKey: queryKeys.organization.locations(),
    queryFn: organizationApi.locations,
    enabled: open,
    staleTime: 10 * 60_000,
  });

  const create = useMutation({
    mutationFn: async (values: FormValues) => {
      const employee = await employeesApi.create({
        employeeNumber: values.employeeNumber.trim(),
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        phone: values.phone.trim() || undefined,
        jobTitle: values.jobTitle.trim() || undefined,
        departmentId: values.departmentId || undefined,
        locationId: values.locationId || undefined,
        joinedOn: values.joinedOn || undefined,
      });

      /*
       * The photo is a second request, because it has nowhere to go until the employee exists.
       *
       * A failure here does not fail the creation: the person has been added, and losing that
       * because an image did not upload would be the worse outcome by far. The photo can be set
       * again from their profile, and the toast says so rather than leaving the user guessing why
       * the picture is missing.
       */
      if (photo) {
        try {
          return await employeesApi.uploadPhoto(employee.id!, photo);
        } catch {
          toast.warning('The photo could not be uploaded', {
            description: `${employee.fullName} was added. Try the photo again from their profile.`,
          });
        }
      }
      return employee;
    },
    onSuccess: (employee) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.employees.all });
      toast.success('Employee added', { description: employee.fullName ?? undefined });
      reset();
      setPhoto(null);
      onCreated(employee);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.fieldErrors.length > 0) {
        error.fieldErrors.forEach((fieldError) =>
          setError(fieldError.field as keyof FormValues, { message: fieldError.message }),
        );
        setSubmitError(null);
      } else {
        setSubmitError(error);
      }
    },
  });

  const close = () => {
    if (create.isPending) return;
    setSubmitError(null);
    reset();
    setPhoto(null);
    onClose();
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={close}
      title="Add employee"
      description="Creates a person assets can be assigned to."
      busy={create.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={create.isPending}
            onClick={handleSubmit((values) => create.mutate(values))}
          >
            Add employee
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={handleSubmit((values) => create.mutate(values))}
        noValidate
      >
        {submitError ? <InlineError error={submitError} /> : null}

        <PhotoPicker value={photo} onChange={setPhoto} disabled={create.isPending} />

        <TextInput
          label="Employee number"
          required
          placeholder="EMP-0001"
          description="Your organisation's own identifier. It cannot be changed later."
          error={errors.employeeNumber?.message}
          {...register('employeeNumber', {
            required: 'An employee number is required',
            pattern: {
              value: /^[A-Za-z0-9._/-]+$/,
              message: 'Only letters, digits and . _ / - are allowed',
            },
          })}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <TextInput
            label="First name"
            required
            error={errors.firstName?.message}
            {...register('firstName', { required: 'A first name is required' })}
          />
          <TextInput
            label="Last name"
            required
            error={errors.lastName?.message}
            {...register('lastName', { required: 'A last name is required' })}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <TextInput
            label="Email"
            type="email"
            required
            error={errors.email?.message}
            {...register('email', {
              required: 'An email address is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid address' },
            })}
          />
          <TextInput label="Phone" type="tel" {...register('phone')} />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <TextInput label="Job title" {...register('jobTitle')} />
          <TextInput label="Joined on" type="date" {...register('joinedOn')} />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Department"
            placeholder="Not set"
            options={[
              { value: '', label: 'Not set' },
              ...(departments.data ?? []).map((department) => ({
                value: department.id ?? '',
                label: department.name ?? '',
              })),
            ]}
            {...register('departmentId')}
          />
          <div className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
              <Select
                label="Location"
                placeholder="Not set"
                options={[
                  { value: '', label: 'Not set' },
                  ...(locations.data ?? []).map((location) => ({
                    value: location.id ?? '',
                    label: location.name ?? '',
                  })),
                ]}
                {...register('locationId')}
              />
            </div>
            {canEditOrg ? (
              <Button
                variant="secondary"
                aria-label="Add a location"
                title="Add a location"
                onClick={() => setAddingLocation(true)}
                className="mb-px h-9 shrink-0 px-3"
              >
                <Plus className="size-4" />
              </Button>
            ) : null}
          </div>
        </div>
      </form>
    </Dialog>

    <QuickLocationDialog
      open={addingLocation}
      onClose={() => setAddingLocation(false)}
      onCreated={(location) => {
        setAddingLocation(false);
        setValue('locationId', location.id ?? '', { shouldValidate: true });
      }}
    />
    </>
  );
}
